import React, { useCallback, useEffect, useState } from 'react'
import { PersonalEmailWarning } from './PersonalEmailWarning'
import { useHistory } from 'react-router-dom'
import { Grid } from '@material-ui/core'
import { debounce } from '../../../../../../Utils/Functions'
import Select from '../../../../../../Components/Inputs/Select'
import Phone from '../../../../../../Components/Inputs/Phone'
import Input from '../../../../../../Components/Inputs/Input'
import SubmitButton from '../../../../../../Components/Form/SubmitButton'
import useQuery from '../../../../../../Utils/Hooks/useQuery'
import { useWrappedPosthog } from '../../../../../../Components/PostHogProvider/hooks'
import { useSelector } from 'react-redux'
import { authorization } from '../../../../../../Module/Auth/Auth'
import { checkIfClientExist } from '../../../graphQL'
import { PATHS } from 'Config/Routing/paths'
import { AUTH_METHOD } from '../../../../../../constants'
import BounceLoader from '../../../../../../Components/BounceLoader'
import { getIsEddmUser } from '../../../../../../Utils/User'
import { getInitialSignupData, STEPS } from '../../../options'
import './Steps.css'

export const Step2 = ({
  fields,
  setFields,
  onChange,
  countries,
  onSubmit,
  isShow,
  authMethod,
  shouldRestrictToEddmOnly,
  setStep,
}) => {
  const history = useHistory()
  const query = useQuery()
  const [disabled, setDisabled] = useState(true)
  const [phoneCountry, setPhoneCountry] = useState('us')
  const [isChecking, setIsChecking] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const posthog = useWrappedPosthog()
  const user = useSelector(state => state.auth.user)

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !disabled) {
      onSubmit(e)
    }
  }

  const onFieldChange = type => value => {
    if (posthog && type === 'countryId') {
      posthog.capture('Country Selected', {
        'event_category': 'Signup',
      })
    }
    onChange(type)(value, false)
  }

  const debounceValidation = useCallback(
    debounce(() => {
      setIsTyping(false)
    }, 500),
    [],
  )

  const handleInputChange = type => value => {
    setIsTyping(true)
    debounceValidation()
    onFieldChange(type)(value)
  }

  useEffect(() => {
    let stepFields = Object.keys(fields).reduce((acc, key) => {
      if (
        ['countryId', 'phone', 'firstName', 'lastName', 'companyName'].includes(
          key,
        )
      ) {
        acc[key] = fields[key]
      }
      return acc
    }, {})
    const isFieldsHasError = Object.values(stepFields).some(
      ({ error, additionalError }) => error || additionalError,
    )

    const isCompanyNameValid = fields.companyName.value.length >= 2

    if (isFieldsHasError || isChecking || isTyping || !isCompanyNameValid) {
      setDisabled(true)
    } else {
      setDisabled(false)
    }
  }, [fields, isChecking])

  useEffect(() => {
    if (countries.length > 0 && !fields.countryId.value) {
      const getIPDetails = async () => {
        try {
          const url = 'https://api.ipify.org?format=json'
          const ipResponse = await fetch(url)
          const ipData = await ipResponse.json()
          const response = await fetch(
            process.env.REACT_APP_OPPIZI_IP_INFO_URL +
              `?ip=${ipData.ip}&key=oppizi`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
              },
            },
          )
          const { countryName, countryCode } = await response.json()
          const userCountry = countries.find(({ name }) => name === countryName)
          if (countryName && userCountry) {
            onChange('countryId')(userCountry.value, false)
            setPhoneCountry(countryCode.toLowerCase())
          }
        } catch (error) {}
      }
      getIPDetails()
    }
  }, [countries])

  const onCheckExistClient = useCallback(
    debounce(async companyName => {
      if (companyName.length < 2) {
        setIsChecking(false)
        return
      }
      setIsChecking(true)
      const response = await checkIfClientExist(companyName)
      const {
        data: { isExistClient },
      } = await response.json()

      onChange('companyName')(
        null,
        false,
        isExistClient
          ? 'An account with this company name already exists. If you believe this is a mistake, please contact us.'
          : false,
      )
      setIsChecking(false)
    }, 1000),
    [],
  )

  useEffect(() => {
    if (!fields.companyName.error) {
      onCheckExistClient(fields.companyName.value)
    }
    if (fields.firstName.value) {
      onChange('firstName')(fields.firstName.value, false)
    }
    if (fields.lastName.value) {
      onChange('lastName')(fields.lastName.value, false)
    }
  }, [
    fields.companyName.value.length,
    fields.firstName.value,
    fields.lastName.value,
  ])

  const getToolsParams = () => {
    try {
      const oppizi_source = query.get('oppizi_source')
      const qr_code_id = query.get('qr_code_id')
      return { oppizi_source, qr_code_id }
    } catch (error) {
      return { oppizi_source: null, qr_code_id: null }
    }
  }

  const { qr_code_id, oppizi_source } = getToolsParams()
  const isEDDMUser = getIsEddmUser(oppizi_source)

  useEffect(() => {
    if (authorization.isLogged()) {
      if (qr_code_id && qr_code_id.length) {
        const user = authorization.getUser()
        if (user && user.client && user.client.id) {
          return window.open(
            `https://tools.oppizi.com/qrcodes?client_id=${
              user.client.id
            }&qr_code_id=${qr_code_id}`,
            '_self',
          )
        }
      } else {
        return history.push(PATHS.main.dashboard)
      }
    }
  }, [user])

  return (
    <div
      style={{ display: isShow ? 'block' : 'none' }}
      onKeyDown={handleKeyPress}
    >
      <h1 style={{ textAlign: 'left' }}>Complete your account info</h1>
      {shouldRestrictToEddmOnly && authMethod === AUTH_METHOD.SOCIAL && (
        <div style={{ marginTop: '10px' }}>
          <PersonalEmailWarning marginTop="10px" />
          <div
            className="switchToWorkEmail"
            onClick={() => {
              setFields(getInitialSignupData(isEDDMUser, query))
              setStep(STEPS.STEP_1)
            }}
          >
            Switch to a work email
          </div>
        </div>
      )}
      <Grid container spacing={2} style={{ marginTop: '24px' }}>
        <Grid item xs={12} md={6} style={{ padding: '0px 8px' }}>
          <Input
            label="First name"
            value={fields.firstName.value}
            onChange={onChange('firstName')}
            required
            onlyString
            minLength={2}
            className="inputMargin"
            cypress_id={'firstNameInput'}
          />
        </Grid>
        <Grid item xs={12} md={6} style={{ padding: '0px 8px 8px' }}>
          <Input
            label="Last name"
            value={fields.lastName.value}
            onChange={onChange('lastName')}
            required
            onlyString
            minLength={2}
            className="inputMargin"
            cypress_id={'lastNameInput'}
            isTouched={authMethod === AUTH_METHOD.SOCIAL}
          />
        </Grid>
      </Grid>
      {!isEDDMUser && !shouldRestrictToEddmOnly && (
        <Select
          label="Country"
          value={countries.length ? fields.countryId.value : ''}
          onChange={countryId => {
            onFieldChange('countryId')(countryId)
            let phoneCountry = countries
              .find(({ value }) => countryId === value)
              .code.toLowerCase()
            if (phoneCountry === 'uk') {
              phoneCountry = 'gb'
            }
            setPhoneCountry(phoneCountry)
          }}
          options={countries}
          cypress_id={'countryDropdown'}
        />
      )}
      <Phone
        label="Phone number"
        country={phoneCountry}
        value={fields.phone.value}
        onChange={onChange('phone')}
        onCountryChange={({ countryCode }) => {
          setPhoneCountry(countryCode)
        }}
        required
        cypress_id={'phoneInput'}
      />
      <Input
        label="Company name"
        value={fields.companyName.value}
        additionalError={fields.companyName.additionalError}
        onChange={handleInputChange('companyName')}
        required
        minLength={2}
        className="inputMarginStep3"
        cypress_id={'companyNameInput'}
      />
      <Grid container alignItems="center">
        <Grid style={{ width: '100%' }}>
          <SubmitButton
            className={'signUpButton'}
            type="submit"
            disabled={disabled}
            onClick={onSubmit}
            cypress_id={'nextBtnOnSignup'}
          >
            {isChecking ? (
              <BounceLoader size={24} />
            ) : (
              'Create account for free'
            )}
          </SubmitButton>
        </Grid>
        <div className={'link'}>
          By creating an account, you agree to our{' '}
          <a href="https://oppizi.com/terms-of-service" className={'links'}>
            Terms of Use
          </a>{' '}
          and{' '}
          <a href="https://www.oppizi.com/privacy-policy/" className={'links'}>
            Privacy Policy
          </a>
          .
        </div>
      </Grid>
    </div>
  )
}
